import React, { useState, useEffect, useCallback } from 'react';
import AddUsers from './AddUsers';
import config from "../../common/Config";
import { toast } from "sonner";
import ExportUserList from './ExportUserList';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  InputAdornment,
  TablePagination,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Skeleton,
  ListItemIcon,
  ListItemText,
  Grid2,
  Tabs,
  Tab,
} from '@mui/material';
import { Iconify } from '../../Minimal/src/components/iconify/iconify';
import '../../common/commonStyles.css';
import { postGetUsersList } from '../../../utils/usersApi';

const Users = () => {
  const [searchValue, setSearchValue] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const accessToken = localStorage.getItem('accessToken');
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [menuAnchor, setMenuAnchor] = useState({ el: null, row: null });
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const token = localStorage.getItem("token");

  const statusFilter = tabValue === 0 ? 1 : 0;

  const fetchData = useCallback(async () => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    setLoading(true);
    try {
      const json = await postGetUsersList(`${config.API_URL}/get_users_list`, token, {
        draw: 1,
        start: 0,
        length: 10000,
        searchValue: searchValue || '',
        status: statusFilter,
      });

      if (json?.data && Array.isArray(json.data)) {
        const filtered = json.data.filter((row) =>
          statusFilter === 1 ? row.active == "1" : row.active != "1"
        );
        setTotalUsers(filtered.length);
        const start = page * rowsPerPage;
        setUsers(filtered.slice(start, start + rowsPerPage));
      } else {
        setUsers([]);
        setTotalUsers(0);
      }
    } catch (error) {
      toast.error("Failed to fetch users");
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchValue, accessToken, token, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUserAdded = () => {
    fetchData();
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setSearchValue(value);
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleChangeStatus = async (id, status) => {
    const formData = new FormData();
    formData.append('status', status);
    formData.append('id', id);
    const apiUrl = `${config.API_URL}/update_users_status`;
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to change status');
      }

      await response.json();
      toast.success('Status changed successfully.');
      fetchData();
    } catch (error) {
      toast.error('Failed to change status.');
    }
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setMenuAnchor({ el: null, row: null });
  };

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setMenuAnchor({ el: event.currentTarget, row });
  };

  const handleMenuAction = (action, row) => {
    handleMenuClose();

    if (action === 'edit') {
      window.location.href = `${config.BASE_URL}/user/edit_profile/${row.id}`;
    } else if (action === 'settings') {
      window.location.href = `${config.BASE_URL}/settings/change_permissions/${row.id}`;
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <Box>
      <AddUsers onUserAdded={handleUserAdded} open={addUserDialogOpen} onClose={() => setAddUserDialogOpen(false)} />
      <Box className="page-container">
          <Paper className="paper-style">
          
            <Box sx={{ px:2, py:1.2}}>
              <Grid2 container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Grid2 size={{ xs: 12, md: 2 }}>
                <Typography className="page-title">
                  Users
                </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 4 }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    className="common-tabs"
                  >
                    <Tab label="Active" />
                    <Tab label="Inactive" />
                  </Tabs>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }} gap={1} display="flex" flexDirection={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} justifyContent="flex-end" flexWrap="nowrap" mt={{ xs: 2, md: 0 }}>
                  <TextField
                    className="input-border"
                    size="small"
                    placeholder="Search users..."
                    value={searchInputValue}
                    onChange={handleSearchChange}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Iconify icon="eva:search-outline"/>
                          </InputAdornment>
                        )
                      }
                    }}
                    sx={{
                      width: { xs: '100%', md: 220 },
                      minWidth: { md: 180 },
                      flexShrink: 1,
                    }}
                  />
                  <ExportUserList totalUsers={totalUsers} urlLink="get_users_list" status={statusFilter} />
                  <Button
                    variant="contained"
                    className="button-variant-contained"
                    onClick={() => setAddUserDialogOpen(true)}
                    startIcon={<Iconify icon="mingcute:add-line" width={16} />}
                    sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    Add User
                  </Button>
                </Grid2>
              </Grid2>
            </Box>
            <TableContainer
        
            >
              <Table
                size="medium"
                className="table-cell-border"
                sx={{
                  tableLayout: "auto" }}
              >
                <TableHead>
                  <TableRow className="table-cell-header">
                    {[
                      "#",
                      "User Name",
                      "First Name",
                      "Last Name",
                      "Email",
                      "Default Role",
                      "Multiple Role",
                      "Action"
                    ].map((header) => {
                      let align = "left";
                      if (header === "#" || header === "Action") {
                        align = "center";
                      }
                      return (
                        <TableCell
                          key={header}
                          align={align}
                          sx={{   userSelect: 'none',
                          }}
                        >
                          {header}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell colSpan={8}>
                          <Skeleton height={40} />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No records found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((row, index) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(145, 158, 171, 0.04)"
                          },
                   
                          "& .MuiTableCell-root": {
                            fontSize: "0.75rem",
                            color: "#212b36",
                            py: 1,
                          },
                        }}

                      >
                        <TableCell align="center">
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell align="left">
                          {row.username}
                        </TableCell>
                        <TableCell align="left">
                          {row.first_name}
                        </TableCell>
                        <TableCell align="left">
                          {row.last_name}
                        </TableCell>
                        <TableCell align="left">
                          {row.email ? row.email.toLowerCase() : ''}
                        </TableCell>
                        <TableCell align="left">
                          {row.name}
                        </TableCell>
                        <TableCell align="left">
                          {row.roles}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1, fontSize: "0.75rem", color: "#212b36" }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, row)}
                            sx={{
                              color: "#637381",
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)"
                              }
                            }}
                          >
                            <Iconify icon="eva:more-vertical-fill" width={18} height={18} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {users.length > 0 && (
              <Box display="flex" justifyContent="flex-end" alignItems="center">
                <TablePagination
                  component="div"
                  count={totalUsers}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  className="pagination-styles"
                />
              </Box>
            )}
          </Paper>
      </Box>
      <Menu
        anchorEl={menuAnchor.el}
        open={Boolean(menuAnchor.el)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          onClick: (e) => e.stopPropagation(),
          sx: {
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            mt: 1,
          },
        }}
      >
        {menuAnchor.row && (
          <>
            <MenuItem
              onClick={() => {
                const row = menuAnchor.row;
                handleMenuClose();
                if (row) {
                  handleChangeStatus(row.id, row.active == "1" ? 0 : 1);
                }
              }}
              sx={{
                color: menuAnchor.row.active == "1" ? "#FF5630" : "#22C55E",
                "& .MuiListItemIcon-root": {
                  color: menuAnchor.row.active == "1" ? "#FF5630" : "#22C55E",
                },
                "& .MuiTypography-root": {
                  color: menuAnchor.row.active == "1" ? "#FF5630 !important" : "#22C55E !important",
                  fontWeight: 600,
                },
              }}
            >
              <ListItemIcon>
                <Iconify icon="la:toggle-on" width={20} height={20} />
              </ListItemIcon>
              <ListItemText>
                {menuAnchor.row.active == "1" ? "Deactivate" : "Activate"}
              </ListItemText>
            </MenuItem>
            <MenuItem 
              onClick={() => handleMenuAction('edit', menuAnchor.row)}
            >
              <ListItemIcon>
                <Iconify icon="fa-regular:edit" width={20} height={20} />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem 
              onClick={() => handleMenuAction('settings', menuAnchor.row)}
            >
              <ListItemIcon>
                <Iconify icon="material-symbols:settings" width={20} height={20} />
              </ListItemIcon>
              <ListItemText>Permissions</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

    </Box>
  );
};

export default Users;
